import type { components } from "@/api-schema";
import { type GroupMembershipSelectionState, mergeUsersByUuid } from "./group-membership-selection.helpers";

type UserWithMembership = components["schemas"]["UserWithMembershipResponseDto"];

export function isInitialMembershipUser(user: UserWithMembership, initialMemberUuidSet: ReadonlySet<string>): boolean {
  return initialMemberUuidSet.has(user.uuid) || user.hasMembership;
}

export function resolveSelectedUsers(
  selected: UserWithMembership[] | string[],
  currentPageUsers: UserWithMembership[],
): UserWithMembership[] {
  const selectedUuids = selected.filter((selectedItem): selectedItem is string => typeof selectedItem === "string");

  if (selectedUuids.length === 0) {
    return selected as UserWithMembership[];
  }

  const selectedUuidSet = new Set(selectedUuids);

  return currentPageUsers.filter((user) => selectedUuidSet.has(user.uuid));
}

export function applyUserSelectionChange(
  previousState: GroupMembershipSelectionState,
  user: UserWithMembership,
  shouldSelect: boolean,
  initialMemberUuidSet: ReadonlySet<string>,
): GroupMembershipSelectionState {
  if (isInitialMembershipUser(user, initialMemberUuidSet)) {
    const nextRemovedUuids = new Set(previousState.removedInitialUserUuids);

    if (shouldSelect) {
      nextRemovedUuids.delete(user.uuid);
    } else {
      nextRemovedUuids.add(user.uuid);
    }

    return {
      ...previousState,
      addedUsers: previousState.addedUsers.filter((addedUser) => addedUser.uuid !== user.uuid),
      removedInitialUserUuids: Array.from(nextRemovedUuids),
    };
  }

  return {
    ...previousState,
    addedUsers: shouldSelect
      ? mergeUsersByUuid(previousState.addedUsers, [user])
      : previousState.addedUsers.filter((addedUser) => addedUser.uuid !== user.uuid),
  };
}
